import React from "react";
import { MONTHS } from "../config";
import { Link } from "react-router-dom";
import { FiShield, FiTrash2, FiLock, FiInfo, FiMail } from "react-icons/fi";

function Privacy() {
    const now = new Date();
    const last_update = MONTHS[now.getMonth()] + ' ' + now.getFullYear();
    return (
        <div className="pt-24 pb-16 text-[#78350f] min-h-screen">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-12 text-center">
                    <div className="inline-block mb-6">
                        <div className="w-24 h-1 bg-gradient-to-r from-white via-[#fffbeb] to-white mx-auto rounded-full shadow-lg shadow-white/50"></div>
                    </div>
                    <div className="inline-block p-4 bg-blue-600/20 rounded-full mb-4">
                        <FiShield className="text-blue-400 text-4xl" />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-[#78350f] to-[#92400e] bg-clip-text text-transparent drop-shadow-lg">
                        TipTide Studio Privacy Policy
                    </h1>
                    <p className="text-[#92400e] text-lg">Last updated: {last_update}</p>
                </div>

                {/* Introduction */}
                <div className="bg-gradient-to-br from-[#fbbf24]/90 via-[#f59e0b]/90 to-[#fbbf24]/90 rounded-2xl p-6 md:p-8 mb-8 shadow-lg border border-white/40 backdrop-blur-sm">
                    <h2 className="text-2xl font-semibold mb-4 text-[#78350f]">1. Introduction and Scope</h2>
                    <div className="space-y-3 text-[#92400e] leading-relaxed">
                        <p>
                            Welcome to TipTide Studio. Your privacy is incredibly important to us. This policy clearly outlines how we collect, use, store, and protect your personal information when you use our games and services (collectively, the "Services").
                        </p>
                        <p>
                            By using our Services, you agree to the terms of this Privacy Policy. We are committed to transparency and respecting your rights regarding your data.
                        </p>
                    </div>
                </div>

                {/* Section 2 */}
                <section className="mb-8 bg-gradient-to-br from-[#fbbf24]/90 via-[#f59e0b]/90 to-[#fbbf24]/90 rounded-xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm">
                    <h3 className="text-2xl font-bold mb-4 text-[#78350f] flex items-center gap-2">
                        <span className="bg-white text-[#78350f] rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">2</span>
                        The Information We Collect
                    </h3>
                    <p className="mb-4 text-[#92400e] leading-relaxed">
                        We collect data primarily to provide, maintain, and improve our Services. This data falls into three main categories:
                    </p>

                    {/* Subsection A */}
                    <div className="mb-6">
                        <h4 className="text-lg font-semibold mb-3 text-[#92400e]">A. Data You Provide Directly</h4>
                        <p className="mb-3 text-[#92400e] leading-relaxed">
                            This is information you intentionally give us when creating an account, making a purchase, or contacting support.
                        </p>
                        <ul className="space-y-2 text-[#92400e] ml-4">
                            <li className="flex items-start gap-3">
                                <span className="text-[#78350f] mt-1">•</span>
                                <span><span className="font-semibold text-[#78350f]">Contact and Account Data:</span> Your name, username, email address, user ID, and password/account credentials.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-[#78350f] mt-1">•</span>
                                <span><span className="font-semibold text-[#78350f]">Communication Data:</span> Records of your correspondence when you interact with our customer support team or participate in our events.</span>
                            </li>
                        </ul>
                    </div>

                    {/* Subsection B */}
                    <div className="mb-6">
                        <h4 className="text-lg font-semibold mb-3 text-[#92400e]">B. Data Collected Automatically</h4>
                        <p className="mb-3 text-[#92400e] leading-relaxed">
                            We automatically gather this data when you access and use our games and services.
                        </p>
                        <ul className="space-y-2 text-[#92400e] ml-4">
                            <li className="flex items-start gap-3">
                                <span className="text-[#78350f] mt-1">•</span>
                                <span><span className="font-semibold text-[#78350f]">Device and Connection Information:</span> Your unique Device ID, IP address, operating system, and browser type.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-[#78350f] mt-1">•</span>
                                <span><span className="font-semibold text-[#78350f]">Gameplay and Usage Data (Analytics):</span> Details on how you use our games, including playtime, levels achieved, in-game purchases, and scores. This helps us monitor performance and improve the user experience.</span>
                            </li>
                        </ul>
                    </div>

                    {/* Subsection C */}
                    <div>
                        <h4 className="text-lg font-semibold mb-3 text-[#92400e]">C. Data from Third-Party Sources</h4>
                        <p className="text-[#92400e] leading-relaxed ml-4">
                            If you link your TipTide account to a third-party platform (like Google Play Games, Apple Game Center, or social media), we may receive basic profile information in line with your settings on that service.
                        </p>
                    </div>
                </section>

                {/* Section 3 */}
                <section className="mb-8 bg-gradient-to-br from-[#fbbf24]/90 via-[#f59e0b]/90 to-[#fbbf24]/90 rounded-xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm">
                    <h3 className="text-2xl font-bold mb-4 text-[#78350f] flex items-center gap-2">
                        <span className="bg-white text-[#78350f] rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">3</span>
                        How We Use Your Data
                    </h3>
                    <p className="mb-4 text-[#92400e] leading-relaxed">
                        We use the collected information for the following essential purposes:
                    </p>
                    <ul className="space-y-3 text-[#92400e] ml-4">
                        <li className="flex items-start gap-3">
                            <span className="text-[#78350f] mt-1">•</span>
                            <span><span className="font-semibold text-[#78350f]">To Operate the Services:</span> To enable you to play our games, manage your account, and deliver the features you expect.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-[#78350f] mt-1">•</span>
                            <span><span className="font-semibold text-[#78350f]">Service Improvement:</span> To analyze user behavior and trends, debug issues, and continuously enhance the functionality and performance of our games.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-[#78350f] mt-1">•</span>
                            <span><span className="font-semibold text-[#78350f]">Customer Support:</span> To effectively respond to your requests, questions, and support needs.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-[#78350f] mt-1">•</span>
                            <span><span className="font-semibold text-[#78350f]">Security and Compliance:</span> To prevent fraud, enforce our Terms of Service, and comply with legal obligations and law enforcement requests.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-[#78350f] mt-1">•</span>
                            <span><span className="font-semibold text-[#78350f]">Personalization:</span> To tailor and optimize your in-game experience based on your preferences and gameplay history.</span>
                        </li>
                    </ul>
                </section>

                {/* Section 4 */}
                <section className="mb-8 bg-gradient-to-br from-[#fbbf24]/90 via-[#f59e0b]/90 to-[#fbbf24]/90 rounded-xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm">
                    <h3 className="text-2xl font-bold mb-4 text-[#78350f] flex items-center gap-2">
                        <span className="bg-white text-[#78350f] rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">4</span>
                        Sharing Your Information
                    </h3>
                    <div className="space-y-4 text-[#92400e] leading-relaxed">
                        <p>
                            We do not sell, rent, or trade your personal data for targeted advertising or commercial purposes.
                        </p>
                        <p className="font-semibold text-[#78350f]">
                            We only share your information in the following limited circumstances:
                        </p>
                        <ul className="space-y-3 ml-4">
                            <li className="flex items-start gap-3">
                                <span className="text-[#78350f] mt-1">•</span>
                                <span><span className="font-semibold text-[#78350f]">Legal Necessity:</span> If required by law, court order, or when we believe disclosure is necessary to protect our rights, your safety, or the safety of others.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-[#78350f] mt-1">•</span>
                                <span><span className="font-semibold text-[#78350f]">Service Providers:</span> With trusted third-party vendors who perform services on our behalf (e.g., hosting, data analytics, payment processing). These providers are contractually obligated to protect your data.</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Section 5 */}
                <section className="mb-8 bg-gradient-to-br from-[#fbbf24]/90 via-[#f59e0b]/90 to-[#fbbf24]/90 rounded-xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm">
                    <h3 className="text-2xl font-bold mb-4 text-[#78350f] flex items-center gap-2">
                        <span className="bg-white text-[#78350f] rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">5</span>
                        Security and Data Retention
                    </h3>

                    {/* Data Security */}
                    <div className="mb-6">
                        <div className="flex items-center gap-3 mb-3">
                            <FiShield className="text-[#78350f] text-xl" />
                            <h4 className="text-lg font-semibold text-[#92400e]">Data Security</h4>
                        </div>
                        <p className="text-[#92400e] leading-relaxed ml-8">
                            We implement strong technical and organizational measures (like encryption and access controls) to protect your data from unauthorized access, loss, or misuse. Remember that no system is 100% secure; please also safeguard your account credentials.
                        </p>
                    </div>

                    {/* Data Retention and Deletion */}
                    <div>
                        <div className="flex items-center gap-3 mb-3">
                            <FiTrash2 className="text-[#78350f] text-xl" />
                            <h4 className="text-lg font-semibold text-[#92400e]">Data Retention and Deletion</h4>
                        </div>
                        <p className="mb-3 text-[#92400e] leading-relaxed ml-8">
                            We retain your personal data for as long as your account is active or as necessary to fulfill the purposes described in this policy.
                        </p>
                        <ul className="space-y-2 text-[#92400e] ml-8">
                            <li className="flex items-start gap-3">
                                <span className="text-[#78350f] mt-1">•</span>
                                <span><span className="font-semibold text-[#78350f]">Account Deletion:</span> You can delete your account at any time through the in-game settings or by <Link to="/contact" className="text-[#78350f] hover:text-[#92400e] underline">contacting us</Link>.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-[#78350f] mt-1">•</span>
                                <span><span className="font-semibold text-[#78350f]">Backup:</span> Following deletion, some data may remain on our secure backup servers for up to <span className="font-semibold text-[#78350f]">six (6) months</span> for legal compliance and backup purposes before being permanently removed.</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Section 6 */}
                <section className="mb-8 bg-gradient-to-br from-[#fbbf24]/90 via-[#f59e0b]/90 to-[#fbbf24]/90 rounded-xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm">
                    <h3 className="text-2xl font-bold mb-4 text-[#78350f] flex items-center gap-2">
                        <span className="bg-white text-[#78350f] rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">6</span>
                        Your Data Rights
                    </h3>
                    <p className="mb-4 text-[#92400e] leading-relaxed">
                        As a user of TipTide Services, you have control over your information:
                    </p>
                    <ul className="space-y-3 text-[#92400e] ml-4">
                        <li className="flex items-start gap-3">
                            <span className="text-[#78350f] mt-1">•</span>
                            <span><span className="font-semibold text-[#78350f]">Access, Correction, or Deletion:</span> You have the right to request access to, correction of, or deletion of your personal data.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-[#78350f] mt-1">•</span>
                            <span><span className="font-semibold text-[#78350f]">Account Management:</span> You can delete your account as described in Section 5.</span>
                        </li>
                    </ul>
                </section>

                {/* Section 7 */}
                <section className="mb-8 bg-gradient-to-br from-[#fbbf24]/90 via-[#f59e0b]/90 to-[#fbbf24]/90 rounded-xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm">
                    <h3 className="text-2xl font-bold mb-4 text-[#78350f] flex items-center gap-2">
                        <span className="bg-white text-[#78350f] rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">7</span>
                        Policy Updates
                    </h3>
                    <p className="text-[#92400e] leading-relaxed">
                        We may update this policy periodically to reflect changes in our data practices or legal requirements. We will notify you of significant changes and will always update the "Last Updated" date at the top of the policy. Your continued use of the Services after an update constitutes acceptance of the revised policy.
                    </p>
                </section>

                {/* Contact Section */}
                <div className="bg-gradient-to-br from-[#fbbf24]/90 via-[#f59e0b]/90 to-[#fbbf24]/90 rounded-2xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm text-center">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <FiInfo className="text-[#78350f] text-2xl" />
                        <h3 className="text-xl font-semibold text-[#78350f]">Questions About Our Privacy Policy?</h3>
                    </div>
                    <p className="text-[#92400e] mb-6 leading-relaxed">
                        If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please don't hesitate to reach out to us.
                    </p>
                    <Link
                        to="/contact"
                        className="inline-block px-6 py-3 bg-white hover:bg-[#fffbeb] rounded-lg transition text-[#d97706] font-bold shadow-lg shadow-white/30"
                    >
                        <FiMail className="inline-block mr-2" />
                        Contact Us
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default Privacy;
